import "./PageButtons.scss";

import clsx from "clsx";
import { FC } from "react";

import { ConfirmGroupButton } from "./ConfirmGroupButton";
import { PageLockedButton } from "./PageLockedButton";
import { StudentSubmitButton } from "./StudentSubmitButton";

import { t } from "i18n/i18n";
import { RoundedIconButton } from "mds/components/buttons/RoundedIconButton";
import { ChevronUpIcon } from "mds/icons";
import { useWorksheetLoaded } from "sharedb/ShareDBDoc";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentUserId, selectPageGroupUserForUser } from "store/selectors";
import { useShouldShowStudentSubmitButton } from "utils/page";

type PageButtonsProps = {
  containerHasVerticalScrollbar: boolean;
  pageId: string;
  showBackToTop: boolean;
  scrollBackToTop: () => void;
};

export const PageButtons: FC<PageButtonsProps> = ({
  containerHasVerticalScrollbar,
  pageId,
  showBackToTop,
  scrollBackToTop,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const selectedPage = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const isDocLoaded = useWorksheetLoaded(selectedPage?.worksheet_id);
  const showStudentSubmitButton = useShouldShowStudentSubmitButton(selectedPage, isDocLoaded);
  const { canParticipateInSection } = useCapabilities();
  const showPageLockedButton = canParticipateInSection && selectedPage?.locked_at;
  const pageGroupUser = useAppSelector((s) =>
    selectPageGroupUserForUser(s, selectedPage?.id, userId),
  );
  const showConfirmGroupButton = pageGroupUser && !pageGroupUser.is_ready;
  const showButton = showStudentSubmitButton || showPageLockedButton || showConfirmGroupButton;

  if (!selectedPage || !userId) return null;

  return (
    <>
      {showButton && (
        <div
          className={clsx(
            "page-buttons-divider extended-page-block-width mt-[-10px]",
            // The height of the blue divider determines how far the submit button is from the divider
            // and is different depending on whether the container has a vertical scrollbar or not.
            containerHasVerticalScrollbar ? "min-h-[46px]" : "min-h-[26px]",
          )}
        >
          <div className="h-[2px] w-full max-w-[var(--page-default-max-width)] bg-blue-tint-90" />
        </div>
      )}
      <div
        className={clsx(
          "page-buttons extended-page-block-width pointer-events-none sticky bottom-5 flex justify-end gap-2",
          showBackToTop ? "show-back-to-top" : "hide-back-to-top",
        )}
      >
        {showStudentSubmitButton ? (
          <div className="page-buttons-cta pointer-events-auto">
            <StudentSubmitButton selectedPage={selectedPage} />
          </div>
        ) : showPageLockedButton ? (
          <div className="page-buttons-cta pointer-events-auto">
            <PageLockedButton />
          </div>
        ) : showConfirmGroupButton ? (
          <div className="page-buttons-cta pointer-events-auto">
            <ConfirmGroupButton selectedPage={selectedPage} userId={userId} />
          </div>
        ) : null}
        <RoundedIconButton
          aria-label={t("common.back_to_top")}
          className={clsx(
            "page-buttons-top",
            showButton ? "absolute" : "",
            showBackToTop ? "pointer-events-auto" : "",
          )}
          eventName="Back to top button"
          onClick={scrollBackToTop}
        >
          <ChevronUpIcon />
        </RoundedIconButton>
      </div>
    </>
  );
};
